// --- FILE: src/hooks/usePayroll.js (ENHANCED - INTEGRATED WITH NEW HOOKS) ---
import { useState, useCallback, useMemo } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { useDynamiaERP } from './useDynamiaERP';
// Importación actualizada: Ahora se usa useCsvExport en lugar de useExcelExport
import { useCsvExport } from './useCsvExport'; //

export const usePayroll = () => {
  // === ESTADOS PRINCIPALES ===
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('');
  const [reportMetadata, setReportMetadata] = useState(null);

  // === HOOKS INTEGRADOS ===
  const {
    fetchUnifiedReport,
    unifiedSalesData,
    salesMetadata,
    channelBreakdown,
    getChannelStatistics,
    getPeriodStatistics,
    getExecutiveSummary,
    clearData: clearShopifyData
  } = useDynamiaERP();

  // Se desestructura el hook useCsvExport
  const { 
    exportPayrollReport: exportPayrollCsv, // Renombrado para evitar conflicto y claridad
    exportShiftsOnly: exportShiftsCsv,     // Renombrado para claridad
    exportUnifiedSales: exportSalesCsv,    // Renombrado para claridad
    exporting: exportInProgress,
    exportProgress
  } = useCsvExport(); //

  // === FUNCIÓN PRINCIPAL MEJORADA PARA REPORTES PERSONALIZADOS ===
  const calculateCustomReport = useCallback(async (employees, startDate, endDate, reportOptions = {}) => {
    setLoading(true);
    setError('');
    setReportData(null);
    setReportType('unified_custom');

    try {
      if (!startDate || !endDate) {
        throw new Error("Las fechas de inicio y fin son requeridas.");
      }
      
      const formattedPeriod = `Reporte Unificado: ${format(startDate, 'dd/MM/yy HH:mm')} - ${format(endDate, 'dd/MM/yy HH:mm')}`;
      setReportPeriod(formattedPeriod);

      console.log('🔄 Iniciando cálculo de reporte unificado:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        employees: employees.length,
        options: reportOptions
      });

      let shiftsData = [];
      let salesData = {};
      let reportMetadata = {};

      // === 1. OBTENER DATOS DE TURNOS ===
      if (reportOptions.includeHours) {
        console.log('📊 Obteniendo datos de turnos...');
        
        try {
          // Consulta de Firestore para turnos
          // NOTA: Asegúrate de tener un índice compuesto en Firestore para 'startTime' (desc)
          // y el filtro de rango de fechas para un mejor rendimiento.
          const shiftsQuery = query(
            collection(db, 'shifts'),
            where('startTime', '>=', Timestamp.fromDate(startDate)),
            where('startTime', '<=', Timestamp.fromDate(endDate)),
            orderBy('startTime', 'desc')
          );
          
          const shiftsSnapshot = await getDocs(shiftsQuery);
          shiftsData = shiftsSnapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          }));
          
          console.log(`✅ Turnos obtenidos: ${shiftsData.length}`, {
            byEmployee: shiftsData.reduce((acc, shift) => {
              acc[shift.employeeName] = (acc[shift.employeeName] || 0) + 1;
              return acc;
            }, {})
          });

        } catch (shiftsError) {
          console.warn("⚠️ Error obteniendo turnos, continuando sin datos de horas:", shiftsError);
          // Este error podría indicar la falta de índices o problemas de conexión
          setError("Advertencia: No se pudieron cargar todos los datos de turnos. Verifique sus índices de Firestore.");
        }
      }

      // === 2. OBTENER DATOS UNIFICADOS DE VENTAS ===
      if (reportOptions.includeCommissions) {
        console.log('🛒 Obteniendo datos unificados de ventas...');
        
        try {
          const salesResult = await fetchUnifiedReport(startDate, endDate, {
            includeOnline: true,
            includePOS: true,
            forAdmin: true
          });

          if (salesResult.success) {
            salesData = salesResult.data.sellers || {};
            reportMetadata = {
              ...salesResult.metadata,
              channelBreakdown: salesResult.data.channelBreakdown,
              periodInfo: salesResult.data.periodInfo
            };
            
            console.log('✅ Datos de ventas unificados obtenidos:', {
              totalSellers: Object.keys(salesData).length,
              totalOrders: salesResult.data.totalOrders,
              totalSales: salesResult.data.totalSales,
              channels: Object.keys(salesResult.data.channelBreakdown || {})
            });
          } else {
            throw new Error(salesResult.error || 'Error en datos de ventas');
          }

        } catch (salesError) {
          console.warn("⚠️ Error obteniendo ventas, continuando sin datos de comisiones:", salesError);
          if (!reportOptions.includeHours) {
            // Si solo se solicitaron comisiones y falló, es un error crítico
            throw new Error("No se pudieron obtener los datos de ventas solicitados.");
          }
        }
      }

            // === 3. COMBINAR Y PROCESAR DATOS ===
      console.log('🔗 Combinando datos de turnos y ventas...');

      // Safe normalization: collapse whitespace + lowercase + strip diacritics via unicode escapes
      const normalizeName = (name = '') => {
        if (!name) return '';
        return name
          .trim()
          .replace(/\s+/g, ' ')           // collapse multiple spaces
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, ''); // strip combining diacritical marks
      };

      const salesEntries = Object.entries(salesData || {});
      console.log('📦 Ventas de DynamiaERP:', salesEntries.map(([k]) => k));

      // Build TWO lookup maps:
      // 1. By normalized name (fallback)
      // 2. By DynamiaERP sellerId (primary — most reliable, name-format independent)
      const salesByNormalizedName = new Map();
      const salesById = new Map();

      const mergeSalesData = (existing, incoming) => ({
        ...existing,
        sales:        (existing.sales        || 0) + (incoming.sales        || 0),
        orders:       (existing.orders        || 0) + (incoming.orders       || 0),
        commission:   (existing.commission    || 0) + (incoming.commission   || 0),
        orderDetails: [...(existing.orderDetails || []), ...(incoming.orderDetails || [])],
      });

      salesEntries.forEach(([name, data]) => {
        const key = normalizeName(name);

        // Merge duplicate normalized-name entries (safety net)
        if (salesByNormalizedName.has(key)) {
          salesByNormalizedName.set(key, mergeSalesData(salesByNormalizedName.get(key), data));
        } else {
          salesByNormalizedName.set(key, { ...data, displayName: name });
        }

        // Index by DynamiaERP seller ID
        if (data.sellerId != null) {
          const idKey = String(data.sellerId);
          if (salesById.has(idKey)) {
            salesById.set(idKey, mergeSalesData(salesById.get(idKey), data));
          } else {
            salesById.set(idKey, { ...data, displayName: name });
          }
        }
      });

      let effectiveEmployees = Array.isArray(employees) ? [...employees] : [];
      const fallbackHourlyRate = effectiveEmployees.find(emp => emp.hourlyRate)?.hourlyRate || 10.50;

      // Track which DynamiaERP sellers were matched to a Firestore employee
      const matchedNormalizedNames = new Set();
      const matchedDynamiaIds     = new Set();

      // Helper: find salesData for an employee by ID first, then name
      const findSalesData = (emp) => {
        const dynamiaId = emp.dynamiaSellerId ? String(emp.dynamiaSellerId) : null;

        // 1. Match by DynamiaERP ID (most reliable)
        if (dynamiaId && salesById.has(dynamiaId)) {
          const data = salesById.get(dynamiaId);
          matchedDynamiaIds.add(dynamiaId);
          matchedNormalizedNames.add(normalizeName(data.displayName || ''));
          console.log(`✅ ID match: "${emp.name}" (dynamiaId:${dynamiaId}) → "${data.displayName}"`);
          return data;
        }

        // 2. Match by normalized name
        const key = normalizeName(emp.name || '');
        if (salesByNormalizedName.has(key)) {
          const data = salesByNormalizedName.get(key);
          matchedNormalizedNames.add(key);
          if (data.sellerId != null) matchedDynamiaIds.add(String(data.sellerId));
          console.log(`✅ Name match: "${emp.name}" (key:"${key}") → "${data.displayName}"`);
          return data;
        }

        console.warn(`⚠️ No match for: "${emp.name}" (dynamiaId:${dynamiaId}, key:"${normalizeName(emp.name || '')}")`);
        return null;
      };

      // If no Firestore employees, bootstrap from DynamiaERP sellers
      if (effectiveEmployees.length === 0) {
        effectiveEmployees = salesEntries.map(([name, data]) => ({
          id: data.sellerId ? `dynamia_${data.sellerId}` : `dynamia_${normalizeName(name)}`,
          name,
          hourlyRate: fallbackHourlyRate,
          commissionRate: data.commissionRate ?? 1,
        }));
      }

      // Add DynamiaERP sellers not yet matched to any Firestore employee
      // (run AFTER we know which are matched — we'll do this after the main map)
      const extraSellers = [];
      salesEntries.forEach(([name, data]) => {
        const normalizedKey = normalizeName(name);
        const idKey = data.sellerId != null ? String(data.sellerId) : null;

        const alreadyMatchedByName = matchedNormalizedNames.has(normalizedKey);
        const alreadyMatchedById   = idKey && matchedDynamiaIds.has(idKey);

        // Also check if a Firestore employee has this name (even if not yet processed)
        const existsInFirestore = effectiveEmployees.some(e => normalizeName(e.name) === normalizedKey || (e.dynamiaSellerId && idKey && String(e.dynamiaSellerId) === idKey));

        if (!alreadyMatchedByName && !alreadyMatchedById && !existsInFirestore) {
          extraSellers.push({
            id: idKey ? `dynamia_${idKey}` : `dynamia_${normalizedKey}`,
            name,
            dynamiaSellerId: data.sellerId || null,
            hourlyRate: fallbackHourlyRate,
            commissionRate: data.commissionRate ?? 1,
          });
        }
      });

      const combinedReport = [...effectiveEmployees, ...extraSellers].map(emp => {
        const empSalesRaw = findSalesData(emp);
        const empSalesData = empSalesRaw || { sales: 0, orders: 0, commission: 0, orderDetails: [] };

        const empShifts = shiftsData.filter(shift => shift.employeeId === emp.id);
        const totalMinutes = empShifts.reduce((acc, shift) => acc + (shift.durationMinutes || 0), 0);
        const totalEarnings = (totalMinutes / 60) * (emp.hourlyRate || fallbackHourlyRate);

        const shiftsCount = empShifts.length;
        const canEdit = shiftsCount === 1;
        const editableShift = canEdit ? empShifts[0] : null;

        const totalToPay = parseFloat((totalEarnings + (empSalesData.commission || 0)).toFixed(2));

        const employeeReport = {
          employeeId: emp.id,
          employeeName: emp.name,
          hourlyRate: emp.hourlyRate || fallbackHourlyRate,

          totalHours: totalMinutes / 60,
          totalEarnings: parseFloat(totalEarnings.toFixed(2)),
          shiftsCount,
          shifts: empShifts,

          sales: empSalesData.sales || 0,
          orders: empSalesData.orders || 0,
          commission: empSalesData.commission || 0,
          orderDetails: empSalesData.orderDetails || [],
          legacyOrders: empSalesData.legacyOrders || 0,
          modernOrders: empSalesData.modernOrders || 0,

          totalToPay,

          canEdit,
          editableShift,

          averageShiftHours: shiftsCount > 0 ? (totalMinutes / 60) / shiftsCount : 0,
          averageOrderValue: (empSalesData.orders || 0) > 0
            ? (empSalesData.sales || 0) / empSalesData.orders
            : 0,
          commissionRate: emp.commissionRate || empSalesData.commissionRate || 1,

          hasHours: totalMinutes > 0,
          hasCommissions: (empSalesData.commission || 0) > 0,
          hasActivity: totalMinutes > 0 || (empSalesData.commission || 0) > 0,
        };

        console.log(`?? Procesado empleado ${emp.name}:`, {
          shifts: shiftsCount,
          hours: employeeReport.totalHours,
          sales: employeeReport.sales,
          commission: employeeReport.commission,
          total: totalToPay,
        });

        return employeeReport;
      })
        .filter(emp => emp.hasActivity)
        .sort((a, b) => b.totalToPay - a.totalToPay); // Ordenar por total a pagar (descendente)

      console.log('✅ Reporte combinado generado:', {
        totalEmployees: combinedReport.length,
        totalHours: combinedReport.reduce((acc, emp) => acc + emp.totalHours, 0),
        totalEarnings: combinedReport.reduce((acc, emp) => acc + emp.totalEarnings, 0),
        totalCommissions: combinedReport.reduce((acc, emp) => acc + emp.commission, 0),
        totalToPay: combinedReport.reduce((acc, emp) => acc + emp.totalToPay, 0)
      });

      // Guardar datos en el estado del hook
      setReportData(combinedReport);
      setReportMetadata({
        ...reportMetadata, // Incluye metadatos de Shopify si se obtuvieron
        generatedAt: new Date().toISOString(),
        employeesCount: combinedReport.length,
        shiftsCount: shiftsData.length,
        reportOptions,
        summary: {
          totalHours: combinedReport.reduce((acc, emp) => acc + emp.totalHours, 0),
          totalEarnings: combinedReport.reduce((acc, emp) => acc + emp.totalEarnings, 0),
          totalCommissions: combinedReport.reduce((acc, emp) => acc + emp.commission, 0),
          totalToPay: combinedReport.reduce((acc, emp) => acc + emp.totalToPay, 0)
        }
      });

    } catch (err) {
      console.error("❌ Error calculando reporte personalizado:", err);
      setError(`No se pudo generar el reporte: ${err.message}`);
      setReportData(null);
      setReportMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [fetchUnifiedReport]);

  // === FUNCIÓN PARA EXPORTAR REPORTE ACTUAL (ahora CSV) ===
  const exportCurrentReport = useCallback(async (options = {}) => {
    if (!reportData || reportData.length === 0) {
      throw new Error('No hay datos de reporte para exportar');
    }

    const {
      filename = 'reporte_nomina',
      includeShiftsDetail = true,
      includeSalesDetail = true,
      includeExecutiveSummary = true
    } = options;

    try {
      // Se llama a la función de exportación de CSV para el reporte de nómina
      const result = await exportPayrollCsv(reportData, { //
        filename,
        includeShiftsDetail,
        includeSalesDetail,
        includeExecutiveSummary,
        dateRange: reportPeriod,
        reportType: reportType
      });

      if (result.success) {
        console.log('✅ Reporte exportado exitosamente:', result.filename);
        return {
          success: true,
          // El mensaje ahora indica que son múltiples CSVs si aplica
          message: `Reporte exportado como ${result.filename} (múltiples archivos CSV)` 
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error exportando reporte:', error);
      throw error;
    }
  }, [reportData, reportPeriod, reportType, exportPayrollCsv]); //

  // === FUNCIÓN PARA EXPORTAR TURNOS DE UN EMPLEADO (ahora CSV) ===
  const exportEmployeeShifts = useCallback(async (employeeId, options = {}) => {
    if (!reportData) {
      throw new Error('No hay datos de reporte cargados');
    }

    const employee = reportData.find(emp => emp.employeeId === employeeId);
    if (!employee || !employee.shifts || employee.shifts.length === 0) {
      throw new Error('No se encontraron turnos para este empleado');
    }

    const { filename } = options;
    const employeeName = employee.employeeName.replace(/\s+/g, '_');
    
    try {
      // Se llama a la función de exportación de CSV para turnos
      const result = await exportShiftsCsv( //
        employee.shifts, 
        employee.employeeName,
        {
          filename: filename || `turnos_${employeeName}`,
          dateRange: reportPeriod
        }
      );

      if (result.success) {
        return {
          success: true,
          filename: result.filename,
          message: `Turnos de ${employee.employeeName} exportados como ${result.filename}`
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error exportando turnos del empleado:', error);
      throw error;
    }
  }, [reportData, reportPeriod, exportShiftsCsv]); //

  // === FUNCIÓN PARA EXPORTAR VENTAS UNIFICADAS (ahora CSV) ===
  const exportSalesReport = useCallback(async (options = {}) => {
    if (!unifiedSalesData) {
      throw new Error('No hay datos de ventas unificadas disponibles');
    }

    try {
      // Se llama a la función de exportación de CSV para ventas
      const result = await exportSalesCsv(unifiedSalesData, { //
        filename: options.filename || 'ventas_unificadas',
        includeOrderDetails: options.includeOrderDetails !== false,
        includeChannelBreakdown: options.includeChannelBreakdown !== false
      });

      if (result.success) {
        return {
          success: true,
          // El mensaje ahora indica que son múltiples CSVs si aplica
          message: `Ventas unificadas exportadas como ${result.filename} (múltiples archivos CSV)`
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error exportando ventas unificadas:', error);
      throw error;
    }
  }, [unifiedSalesData, exportSalesCsv]); //

  // === FUNCIÓN PARA OBTENER ESTADÍSTICAS DEL REPORTE ===
  const getReportStatistics = useCallback(() => {
    if (!reportData || !reportMetadata) return null;

    const stats = {
      summary: reportMetadata.summary,
      breakdown: {
        employeesWithHours: reportData.filter(emp => emp.hasHours).length,
        employeesWithCommissions: reportData.filter(emp => emp.hasCommissions).length,
        employeesWithBoth: reportData.filter(emp => emp.hasHours && emp.hasCommissions).length,
        totalShifts: reportData.reduce((acc, emp) => acc + emp.shiftsCount, 0),
        averageHoursPerEmployee: reportData.length > 0 ? 
          reportMetadata.summary.totalHours / reportData.length : 0
      },
      channels: getChannelStatistics(),
      periods: getPeriodStatistics(),
      executive: getExecutiveSummary()
    };

    return stats;
  }, [reportData, reportMetadata, getChannelStatistics, getPeriodStatistics, getExecutiveSummary]);

  // === FUNCIÓN PARA BUSCAR EMPLEADO EN EL REPORTE ===
  const findEmployeeInReport = useCallback((employeeId) => {
    if (!reportData) return null;
    return reportData.find(emp => emp.employeeId === employeeId) || null;
  }, [reportData]);

  // === FUNCIÓN PARA LIMPIAR DATOS ===
  const clearReportData = useCallback(() => {
    setReportData(null);
    setReportMetadata(null);
    setReportType(null);
    setReportPeriod('');
    setError('');
    clearShopifyData();
  }, [clearShopifyData]);

  // === COMPUTED VALUES ===
  const isReportReady = useMemo(() => {
    return reportData && reportData.length > 0;
  }, [reportData]);

  const canExport = useMemo(() => {
    return isReportReady && !loading && !exportInProgress;
  }, [isReportReady, loading, exportInProgress]);

  const reportSummary = useMemo(() => {
    if (!reportData || reportData.length === 0) return null;

    return {
      totalEmployees: reportData.length,
      totalHours: reportData.reduce((acc, emp) => acc + emp.totalHours, 0),
      totalEarnings: reportData.reduce((acc, emp) => acc + emp.totalEarnings, 0),
      totalCommissions: reportData.reduce((acc, emp) => acc + emp.commission, 0),
      totalToPay: reportData.reduce((acc, emp) => acc + emp.totalToPay, 0),
      hasChannelData: !!channelBreakdown,
      hasMetadata: !!reportMetadata
    };
  }, [reportData, channelBreakdown, reportMetadata]);

  return {
    // === ESTADOS PRINCIPALES ===
    loading,
    error,
    reportData,
    reportType,
    reportPeriod,
    reportMetadata,

    // === ESTADOS DERIVADOS ===
    isReportReady,
    canExport,
    reportSummary,
    exportInProgress,
    exportProgress,

    // === DATOS INTEGRADOS DE SHOPIFY ===
    unifiedSalesData,
    salesMetadata,
    channelBreakdown,

    // === FUNCIONES PRINCIPALES ===
    calculateCustomReport,

    // === FUNCIONES DE EXPORTACIÓN (AHORA CSV) ===
    exportCurrentReport,
    exportEmployeeShifts,
    exportSalesReport,

    // === FUNCIONES DE ANÁLISIS ===
    getReportStatistics,
    getChannelStatistics,
    getPeriodStatistics,
    getExecutiveSummary,
    findEmployeeInReport,

    // === UTILIDADES ===
    clearReportData
  };
};



